import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCampaignAnalysis } from '../services/api';

function CampaignAnalysis() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadAnalysis();
  }, [id]);

  const loadAnalysis = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getCampaignAnalysis(id);
      if (response.success) {
        setAnalysis(response);
      } else {
        setError(response.message || 'Failed to load analysis');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load analysis');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0e27] flex items-center justify-center relative z-10">
        <div className="text-center">
          <div className="animate-spin border-4 border-blue-500/30 border-t-blue-500 h-12 w-12 mx-auto rounded-full"></div>
          <p className="mt-4 text-gray-400">Loading analysis...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0e27] flex items-center justify-center relative z-10">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <button
            onClick={loadAnalysis}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-all"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return null;
  }

  const { stats, responseBreakdown, responses } = analysis;

  return (
    <div className="min-h-screen bg-[#0a0e27] py-8 px-4 relative z-10">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <button
            onClick={() => navigate('/campaigns')}
            className="mb-4 flex items-center text-blue-400 hover:text-blue-300 transition-colors text-sm"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Campaigns
          </button>
          <h1 className="text-3xl font-semibold text-white mb-2">Campaign Analysis</h1>
          <p className="text-sm text-gray-400">{analysis.campaign.name}</p>
          {analysis.campaign.status && (
            <div className="mt-3">
              <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                analysis.campaign.status === 'completed' ? 'bg-green-900/30 text-green-400 border border-green-500/30' :
                analysis.campaign.status === 'sending' ? 'bg-yellow-900/30 text-yellow-400 border border-yellow-500/30' :
                analysis.campaign.status === 'failed' ? 'bg-red-900/30 text-red-400 border border-red-500/30' :
                'bg-gray-700/50 text-gray-300 border border-gray-600/30'
              }`}>
                {analysis.campaign.status.charAt(0).toUpperCase() + analysis.campaign.status.slice(1)}
              </span>
              {analysis.stats.progress && (
                <span className="ml-3 text-sm text-gray-400">
                  Progress: {analysis.stats.progress}%
                </span>
              )}
            </div>
          )}
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <div className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg p-6 backdrop-blur-sm">
            <div className="text-sm font-medium text-gray-400 mb-1">Total Recipients</div>
            <div className="text-2xl font-semibold text-white">{stats.totalRecipients}</div>
          </div>
          <div className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg p-6 backdrop-blur-sm">
            <div className="text-sm font-medium text-gray-400 mb-1">Sent</div>
            <div className="text-2xl font-semibold text-white">{stats.sent}</div>
          </div>
          <div className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg p-6 backdrop-blur-sm">
            <div className="text-sm font-medium text-gray-400 mb-1">Opened</div>
            <div className="text-2xl font-semibold text-blue-400">{stats.opened}</div>
            <div className="text-xs text-gray-400 mt-1">{stats.openRate}% open rate</div>
          </div>
          <div className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg p-6 backdrop-blur-sm">
            <div className="text-sm font-medium text-gray-400 mb-1">Responded</div>
            <div className="text-2xl font-semibold text-green-400">{stats.responded}</div>
            <div className="text-xs text-gray-400 mt-1">{stats.responseRate}% response rate</div>
          </div>
          <div className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg p-6 backdrop-blur-sm">
            <div className="text-sm font-medium text-gray-400 mb-1">Total Responses</div>
            <div className="text-2xl font-semibold text-purple-400">
              {Object.values(responseBreakdown).reduce((sum, count) => sum + count, 0)}
            </div>
          </div>
        </div>

        {/* Response Breakdown */}
        <div className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg mb-8 backdrop-blur-sm">
          <div className="px-6 py-4 border-b border-gray-700">
            <h2 className="text-xl font-semibold text-white">Response Breakdown</h2>
          </div>
          <div className="p-6">
            {Object.keys(responseBreakdown).length === 0 ? (
              <p className="text-gray-400 text-center py-8">No responses yet</p>
            ) : (
              <div className="space-y-3">
                {Object.entries(responseBreakdown).map(([response, count]) => (
                  <div key={response} className="flex items-center justify-between p-4 bg-black/50 rounded-lg border border-gray-700">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-white capitalize">{response}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-lg font-semibold text-white">{count}</div>
                      <div className="w-32 bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{
                            width: `${(count / stats.responded) * 100}%`
                          }}
                        ></div>
                      </div>
                      <span className="text-sm text-gray-400 w-12 text-right">
                        {stats.responded > 0 ? ((count / stats.responded) * 100).toFixed(1) : 0}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Response Details Table */}
        <div className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg backdrop-blur-sm">
          <div className="px-6 py-4 border-b border-gray-700">
            <h2 className="text-xl font-semibold text-white">Response Details</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#0a0e27] border-b border-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Response</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Opened</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Responded At</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {responses.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-8 text-center text-gray-400">
                      No responses yet
                    </td>
                  </tr>
                ) : (
                  responses.map((response) => (
                    <tr key={response.id} className="hover:bg-[#0a0e27]/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        {response.email_id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-900/30 text-blue-400 border border-blue-500/30 capitalize">
                          {response.response}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {response.opened ? (
                          <span className="text-green-400">✓ Yes</span>
                        ) : (
                          <span className="text-gray-400">No</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        {new Date(response.responded_at).toLocaleString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CampaignAnalysis;
